<template>
  <v-menu
    :open-on-hover="!$userSettings().doNotShowMenusOnHover"
    top
    offset-y
    content-class="footer-fab-menu"
  >
    <template #activator="{ props }">
      <v-fab-transition>
        <v-btn
          :key="1"
          color="primary"
          size="small"
          icon
          variant="outlined"
          v-bind="props"
          class="pt-0 mt-0 mr-4"
        >
          <!-- V3's `size="small"` icon button auto-shrinks its slotted icon to
               18px; Bridge's `fab` kept the mdi-plus at the default 24px. Pin the
               size so the "+" matches V2. -->
          <v-icon size="24">mdi-plus</v-icon>
        </v-btn>
      </v-fab-transition>
    </template>

    <!-- width pinned +10px over the content-sized 182px: the bullet's visible
         dot is centred in a 24px icon box, so the left edge->dot gap reads wider
         than the true 16px text->right-edge gap. Widening grows the right gap to
         match the left visually. (min-width would clamp to the tiny FAB
         activator; width forces it.) -->
    <v-list width="192">
      <template v-for="(item, index) in addMenuItems" :key="index">
        <!-- Bare divider (matches the left-nav "Select Lists" menu); not wrapped
             in a clickable list-item. `my-2` adds 8px above + below for clearer
             separation between the menu's sections. -->
        <v-divider v-if="item.title === 'divider'" class="my-2" />
        <v-list-item
          v-else
          class="mb-0 mt-0"
          @click="setShowEntityDialog(item)"
        >
          <!-- Leading bullet, matching the left-nav "Select Lists" flyout. -->
          <template #prepend>
            <v-icon>mdi-circle-small</v-icon>
          </template>
          <v-list-item-title>{{ item.title }}</v-list-item-title>
        </v-list-item>
      </template>
    </v-list>
  </v-menu>
</template>

<script lang="ts">
import { defineComponent } from 'vue'
import type { IFeedback } from '~/types/feedback/models'
import type { ICompany } from '~/types/company/models'
import type { ICall } from '~/types/call/models'
import type { ICase } from '~/types/case/models'
import type { IServiceRequest } from '~/types/serviceRequest/models'
import { isServiceRequestsEnabled } from '~/cynqueModules/isServiceRequestsEnabled'
import type { IActivityEmail } from '~/types/email/models'
import type { ITask } from '~/types/task/models'
import type { IMeeting } from '~/types/meeting/models'
import type { IOpportunity } from '~/types/opportunity/models'
import { setShowEntityDialog } from '~/cynqueModules/setShowEntityDialog'
import { addNotification } from '~/cynqueModules/addNotification'
import { eventBus } from '~/cynqueModules/eventBus'
import { getRouteForOnCreated as getRotueForCompanyOnCreated } from '~/services/companies'
import { getRouteForOnCreated as getRouteForOpportunityOnCreated } from '~/services/opportunities'

interface IAddMenuItems {
  title: string
  entityName: string
  afterRequestCallback: Function
}

interface IMenuDivider {
  title: 'divider'
}

export default defineComponent({
  data() {
    const that = this
    return {
      mn: '[components/defaultLayout/FooterPlusMenu.vue]',
      addMenuItems: [
        {
          title: 'User',
          entityName: 'user',
          afterRequestCallback: that.userCreated,
        },
        { title: 'divider' },
        {
          title: 'Task',
          entityName: 'task',
          afterRequestCallback: that.taskCreated,
        },
        {
          title: 'Meeting',
          entityName: 'meeting',
          afterRequestCallback: that.meetingCreated,
        },
        {
          title: 'Email',
          entityName: 'email',
          afterRequestCallback: that.emailCreated,
        },
        {
          title: 'Call',
          entityName: 'call',
          afterRequestCallback: that.callCreated,
        },
        // Off against a live API until it has Service Request endpoints; see
        // cynqueModules/isServiceRequestsEnabled.ts.
        ...(isServiceRequestsEnabled()
          ? [
              {
                title: 'Service Request',
                entityName: 'serviceRequest',
                afterRequestCallback: that.serviceRequestCreated,
              },
            ]
          : []),
        {
          title: 'Case',
          entityName: 'case',
          afterRequestCallback: that.caseCreated,
        },
        { title: 'divider' },
        {
          title: 'Opportunity',
          entityName: 'opportunity',
          afterRequestCallback: that.opportunityCreated,
        },
        {
          title: 'Company',
          entityName: 'company',
          afterRequestCallback: that.companyCreated,
        },
        { title: 'divider' },
        {
          title: 'Feedback',
          entityName: 'feedback',
          afterRequestCallback: function feedbackCreated({
            newEntityData,
          }: {
            newEntityData: IFeedback
          }) {
            addNotification({
              type: 'info',
              message: `Feedback received (ID ${newEntityData.hrid}). Thank you!`,
            })
          },
        },
      ] as (IAddMenuItems | IMenuDivider)[],
    }
  },
  methods: {
    setShowEntityDialog(item: IAddMenuItems | IMenuDivider) {
      console.debug(`${this.mn} setShowEntityDialog | BUTTON CLICKED: FAB '+'`)
      console.debug(this.$route.path)
      setShowEntityDialog({
        callingMn: this.mn,
        entityName: item.entityName,
        desiredAction: 'CREATE',
        afterRequestCallback: item.afterRequestCallback,
        titleOverride: null,
        deleteButtonCaptionsVerbOverride: null,
        subentityAfterRequestCallbacks: undefined,
      })
    },
    companyCreated({ newEntityData }: { newEntityData: ICompany }) {
      // @ts-expect-error
      this.$setSnack({
        callingMn: this.mn,
        snack: {
          message: `Company '${newEntityData.hrid}' created successfully.`,
          timeout: 1500,
        },
      })
      this.$router.push(getRotueForCompanyOnCreated(newEntityData.id))
    },
    opportunityCreated({ newEntityData }: { newEntityData: IOpportunity }) {
      // @ts-expect-error
      this.$setSnack({
        callingMn: this.mn,
        snack: {
          message: `Opportunity '${newEntityData.hrid}' created successfully.`,
          timeout: 1500,
        },
      })
      this.$router.push(getRouteForOpportunityOnCreated(newEntityData.id))
    },
    caseCreated({ newEntityData }: { newEntityData: ICase }) {
      // @ts-expect-error
      this.$setSnack({
        callingMn: this.mn,
        snack: {
          message: `Case '${newEntityData.hrid}' created successfully.`,
          timeout: 1500,
        },
      })
      eventBus.emit('caseCreated', newEntityData)
    },
    serviceRequestCreated({
      newEntityData,
    }: {
      newEntityData: IServiceRequest
    }) {
      // @ts-expect-error
      this.$setSnack({
        callingMn: this.mn,
        snack: {
          message: `Service Request '${newEntityData.hrid}' created successfully.`,
          timeout: 1500,
        },
      })
      eventBus.emit('serviceRequestCreated', newEntityData)
    },
    callCreated({ newEntityData }: { newEntityData: ICall }) {
      // @ts-expect-error
      this.$setSnack({
        callingMn: this.mn,
        snack: {
          message: `Call '${newEntityData.hrid}' created successfully.`,
          timeout: 1500,
        },
      })
      eventBus.emit('callCreated', newEntityData)
    },
    emailCreated({ newEntityData }: { newEntityData: IActivityEmail }) {
      // @ts-expect-error
      this.$setSnack({
        callingMn: this.mn,
        snack: {
          message: `Email '${newEntityData.hrid}' created successfully.`,
          timeout: 1500,
        },
      })
      eventBus.emit('emailCreated', newEntityData)
    },
    meetingCreated({ newEntityData }: { newEntityData: IMeeting }) {
      // @ts-expect-error
      this.$setSnack({
        callingMn: this.mn,
        snack: {
          message: `Meeting '${newEntityData.hrid}' created successfully.`,
          timeout: 1500,
        },
      })
      eventBus.emit('meetingCreated', newEntityData)
    },
    taskCreated({ newEntityData }: { newEntityData: ITask }) {
      // @ts-expect-error
      this.$setSnack({
        callingMn: this.mn,
        snack: {
          message: `Task '${newEntityData.hrid}' created successfully.`,
          timeout: 1500,
        },
      })
      eventBus.emit('taskCreated', newEntityData)
    },
  },
})
</script>

<style>
/* DARK-MODE TEXT + SURFACE, matched to the left-nav "Select Lists" flyout that
   this menu already mirrors structurally (bullets, bare dividers).
   Its items were rendering PURE WHITE: dark's `high-emphasis-opacity` is 1
   (vuetify/composables/theme.js), so `.v-list`'s `rgba(on-surface, high-emphasis)`
   resolves to #FFF -- the same default the flyout had before it was dimmed.
   #AAAAAA is the flyout's value, carried over verbatim so the two menus read as
   one family.
   The surface is lifted #1E1E1E -> #262626. Both menus inherit the app-wide dark
   `surface: '#1E1E1E'` (nuxt.config.ts), which is ALSO what cards are painted --
   so this menu had no edge at all when it opened over one. +8 gives it a lip
   without reading as a different material. #AAAAAA on it is 6.5:1 (7.2:1 on the
   flyout's darker ground).
   Light mode is deliberately untouched. */
.v-theme--dark .footer-fab-menu .v-list {
  background-color: #262626;
}
.v-theme--dark .footer-fab-menu .v-list-item {
  color: #aaaaaa;
}

/* Darken this menu's section dividers (0.12 -> 0.24, ~2x) for clearer
   definition -- scoped to just this menu via its `content-class`. The extra
   `.footer-fab-menu` class out-specifies the global menu-divider rule
   (`.v-menu .v-overlay__content .v-divider…`, which pins all menu dividers to
   0.12 with !important), so only this menu is affected. Non-scoped because the
   menu teleports to the overlay layer, outside this component's DOM.
   ⚠ BOTH THEMES NEED A RULE. global.css defines menu dividers as a PAIR --
   0.12 black for light, 0.12 WHITE for dark -- and the light-only override that
   used to live here beat the dark half of that pair (identical (0,5,0)
   specificity, both !important, so source order decided) and painted a BLACK
   line on the dark menu. Measured in-browser as rgba(0,0,0,0.24): it read as a
   dark groove where the "Select Lists" flyout this menu is matched to shows a
   light one. Mirror the 2x on each side instead.
   The dark selector is COMPOUND (`.v-theme--dark.v-divider`) because Vuetify puts
   the theme class on the divider ELEMENT ITSELF -- same form global.css uses. A
   descendant `.v-theme--dark .v-divider` would not match. */
.v-menu .v-overlay__content.footer-fab-menu .v-divider:not(.v-divider--thick) {
  border-color: rgba(0, 0, 0, 0.24) !important;
}
.v-menu
  .v-overlay__content.footer-fab-menu
  .v-theme--dark.v-divider:not(.v-divider--thick) {
  border-color: rgba(255, 255, 255, 0.24) !important;
}
</style>
